import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemButton,
    Checkbox,
    Paper,
    Grid,
    CircularProgress
} from '@mui/material';
import api from '../../../services/api';
import { useToast } from '../../../context/ToastContext';

interface Trainer {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    trainerSettings?: {
        questionScope?: 'all' | 'assigned';
        assignedLearners?: string[];
    };
}

interface Learner {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
}

interface TrainerDialogProps {
    open: boolean;
    trainer: Trainer | null;
    onClose: (refresh?: boolean) => void;
}

const TrainerDialog: React.FC<TrainerDialogProps> = ({ open, trainer, onClose }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [questionScope, setQuestionScope] = useState<'all' | 'assigned'>('assigned');
    const [assignedLearners, setAssignedLearners] = useState<string[]>([]);
    const [availableLearners, setAvailableLearners] = useState<Learner[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();

    useEffect(() => {
        if (open) {
            fetchLearners();
            if (trainer) {
                // Edit mode
                setFirstName(trainer.firstName);
                setLastName(trainer.lastName);
                setEmail(trainer.email);
                setPassword('');
                setQuestionScope(trainer.trainerSettings?.questionScope || 'assigned');
                setAssignedLearners(trainer.trainerSettings?.assignedLearners || []);
            } else {
                // Create mode
                resetForm();
            }
        }
    }, [open, trainer]);

    const fetchLearners = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/users');
            const learners = res.data.filter((user: any) => user.role === 'learner');
            setAvailableLearners(learners);
        } catch (err) {
            console.error("Error fetching learners", err);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPassword('');
        setQuestionScope('assigned');
        setAssignedLearners([]);
    };

    const handleToggleLearner = (learnerId: string) => {
        setAssignedLearners(prev =>
            prev.includes(learnerId)
                ? prev.filter(id => id !== learnerId)
                : [...prev, learnerId]
        );
    };

    const handleSubmit = async () => {
        // Validation
        if (!firstName || !lastName || !email) {
            showToast('Veuillez remplir tous les champs obligatoires', 'warning');
            return;
        }

        if (!trainer && !password) {
            showToast('Le mot de passe est requis pour créer un formateur', 'warning');
            return;
        }

        if (questionScope === 'assigned' && assignedLearners.length === 0) {
            showToast('Veuillez assigner au moins un apprenant si le scope est "assigné"', 'warning');
            return;
        }

        setSubmitting(true);

        try {
            const payload: any = {
                firstName,
                lastName,
                email,
                role: 'trainer',
                trainerSettings: {
                    questionScope,
                    assignedLearners
                }
            };

            if (password) {
                payload.password = password;
            }

            if (trainer) {
                // Update existing trainer
                await api.put(`/api/users/${trainer._id}`, payload);
            } else {
                // Create new trainer
                await api.post('/api/users', payload);
            }

            onClose(true); // Close and refresh
        } catch (err: any) {
            console.error("Error saving trainer", err);
            showToast(err.response?.data?.message || 'Erreur lors de la sauvegarde', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
            <DialogTitle>
                {trainer ? 'Modifier le formateur' : 'Ajouter un formateur'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Prénom"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Nom"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Mot de passe"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required={!trainer}
                                helperText={trainer ? 'Laissez vide pour ne pas modifier' : 'Requis'}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Scope des questions</FormLabel>
                                <RadioGroup
                                    value={questionScope}
                                    onChange={(e) => setQuestionScope(e.target.value as 'all' | 'assigned')}
                                >
                                    <FormControlLabel
                                        value="all"
                                        control={<Radio />}
                                        label="Toutes les questions de l'organisme"
                                    />
                                    <FormControlLabel
                                        value="assigned"
                                        control={<Radio />}
                                        label="Uniquement les questions des apprenants assignés"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Apprenants assignés ({assignedLearners.length})
                            </Typography>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                                    <List dense>
                                        {availableLearners.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="Aucun apprenant disponible"
                                                    secondary="Créez d'abord des apprenants"
                                                />
                                            </ListItem>
                                        ) : (
                                            availableLearners.map((learner) => (
                                                <ListItem
                                                    key={learner._id}
                                                    disablePadding
                                                >
                                                    <ListItemButton onClick={() => handleToggleLearner(learner._id)} dense>
                                                        <ListItemIcon>
                                                            <Checkbox
                                                                edge="start"
                                                                checked={assignedLearners.includes(learner._id)}
                                                                tabIndex={-1}
                                                                disableRipple
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={`${learner.firstName} ${learner.lastName}`}
                                                            secondary={learner.email}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))
                                        )}
                                    </List>
                                </Paper>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()} disabled={submitting}>
                    Annuler
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : null}
                >
                    {trainer ? 'Enregistrer' : 'Créer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TrainerDialog;
